const DEFAULT_TTL_MS = 10 * 60 * 1000;
const DEFAULT_MAX_ENTRIES = 128;

export interface NotificationResponseIdentity {
  requestIdentifier: string;
  actionIdentifier: string;
  occurrenceRunAt?: string | null;
  notificationDate?: number | null;
}

/**
 * Build an occurrence-aware response key. Notification request identifiers are
 * intentionally reused when a recurring reminder is rescheduled, so they are
 * not sufficient on their own.
 */
export function notificationResponseKey(identity: NotificationResponseIdentity): string {
  const occurrence = typeof identity.occurrenceRunAt === 'string'
    ? identity.occurrenceRunAt.trim()
    : '';
  const deliveredAt = Number.isFinite(identity.notificationDate)
    ? String(identity.notificationDate)
    : 'unknown';

  return [
    identity.requestIdentifier,
    identity.actionIdentifier,
    occurrence || deliveredAt,
  ].join(':');
}

/**
 * Small TTL cache used only to collapse the duplicate native-listener and
 * cold-start delivery of the same notification response.
 */
export class NotificationResponseDedupe {
  private readonly seen = new Map<string, number>();

  constructor(
    private readonly ttlMs = DEFAULT_TTL_MS,
    private readonly maxEntries = DEFAULT_MAX_ENTRIES,
  ) {}

  claim(key: string, now = Date.now()): boolean {
    for (const [candidate, seenAt] of this.seen) {
      if (now - seenAt >= this.ttlMs) this.seen.delete(candidate);
    }

    if (this.seen.has(key)) return false;
    this.seen.set(key, now);

    while (this.seen.size > this.maxEntries) {
      const oldest = this.seen.keys().next().value as string | undefined;
      if (!oldest) break;
      this.seen.delete(oldest);
    }

    return true;
  }
}
